import { useContext } from "react";
import { PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH, PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT } from "../../functions/src/api/constants";
import { langContext } from "../api/Lang";
import { platformSupportReasonOptionsEl } from "./platform-support-reason-options";

const el = {
    navLabel: "Υποστήριξη",
    pageHeading: "Υποστήριξη πλατφόρμας Lunchbox",
    mealServiceTitle: "Γεύματα, παραγγελίες & παράδοση",
    mealServiceBody:
        "Για θέματα σχετικά με το φαγητό, τις ποσότητες, την παράδοση ή την ακύρωση παραγγελίας, επικοινώνησε απευθείας με την εταιρεία σίτισης του σχολείου σου.",
    mealServiceEmailPrefix: "Email: ",
    mealServicePhonePrefix: "Τηλέφωνο: ",
    mealServiceContactMissing: "Τα στοιχεία επικοινωνίας της εταιρείας σίτισης δεν είναι διαθέσιμα αυτή τη στιγμή. Ρώτησε το σχολείο σου.",
    platformSectionTitle: "Πρόβλημα με την εφαρμογή;",
    platformSectionBody:
        "Χρησιμοποίησε τη φόρμα μόνο για τεχνικά θέματα της πλατφόρμας (σύνδεση, πληρωμές, σφάλματα στην ιστοσελίδα).",
    formPanelRegionLabel: "Λεπτομέρειες αίτησης υποστήριξης",
    formPanelExpandedAnnouncement: "Εμφανίστηκαν τα υπόλοιπα πεδία της φόρμας.",
    reasonLabel: "Λόγος",
    reasonPlaceholder: "Επίλεξε λόγο",
    reasonOptions: platformSupportReasonOptionsEl,
    messageLabel: "Περιγραφή",
    messagePlaceholderDefault: "Περίγραψε το πρόβλημα όσο πιο συγκεκριμένα μπορείς…",
    messagePlaceholders: {
        technical: "Π.χ. η σελίδα δεν φορτώνει, βλέπω σφάλμα όταν…",
        signIn: "Π.χ. δεν μπορώ να συνδεθώ, δεν έρχεται email επαλήθευσης…",
        payment: "Π.χ. δεν μπορώ να ολοκληρώσω την πληρωμή, το πορτοφόλι δεν ενημερώνεται…",
        ordersPackages: "Π.χ. δεν μπορώ να υποβάλω παραγγελία, πρόβλημα με πακέτο…",
        other: "Περίγραψε το πρόβλημα όσο πιο συγκεκριμένα μπορείς…",
    },
    messageRequired: "Συμπλήρωσε την περιγραφή του προβλήματος.",
    messageTooShort: "Η περιγραφή είναι πολύ σύντομη — πρόσθεσε λεπτομέρειες.",
    messageTooLong: `Η περιγραφή δεν μπορεί να ξεπερνά τα ${PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH} χαρακτήρες.`,
    messageCharCount: (current: number, min: number, max: number) =>
        current < min ? `${current} / ${max} (τουλάχιστον ${min})` : `${current} / ${max}`,
    snapshotsLabel: "Στιγμιότυπα οθόνης",
    snapshotsHint: `Προαιρετικά — έως ${PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT} εικόνες (JPEG/PNG/WebP).`,
    snapshotsAdd: "Προσθήκη εικόνας",
    snapshotPreviewAlt: (oneBased: number) => `Προεπισκόπηση στιγμιότυπου ${oneBased}`,
    snapshotsRemove: "Αφαίρεση",
    snapshotsRemoveFor: (oneBased: number) => `Αφαίρεση στιγμιότυπου ${oneBased}`,
    snapshotsUploading: "Μεταφόρτωση…",
    snapshotsInvalidType: "Δέχονται μόνο εικόνες JPEG, PNG ή WebP.",
    snapshotsTooLarge: "Η εικόνα είναι πολύ μεγάλη — δοκίμασε μικρότερο στιγμιότυπο.",
    snapshotsUploadFailed: "Δεν ήταν δυνατή η μεταφόρτωση της εικόνας. Δοκίμασε ξανά.",
    yourEmailLabel: "Θα σου απαντήσουμε στο email του λογαριασμού σου:",
    submit: "Αποστολή",
    submitting: "Αποστολή…",
    successTitle: "Το μήνυμά σου εστάλη",
    successBody: "Θα σου απαντήσουμε στο email του λογαριασμού σου το συντομότερο δυνατό.",
    sendAnother: "Νέο μήνυμα",
    submitFailed: "Δεν ήταν δυνατή η αποστολή. Δοκίμασε ξανά αργότερα.",
    privacyPrefix: "Για αιτήματα προστασίας προσωπικών δεδομένων, δες την ",
    privacyLinkText: "Πολιτική Απορρήτου",
    leaveDraftTitle: "Έξοδος χωρίς αποστολή;",
    leaveDraftMsg: "Αν φύγεις τώρα, το μήνυμά σου και τα στιγμιότυπα θα διαγραφούν.",
    leaveDraftYes: "Έξοδος",
};

const en: typeof el = {
    navLabel: "Support",
    pageHeading: "Lunchbox platform support",
    mealServiceTitle: "Meals, orders & delivery",
    mealServiceBody:
        "For questions about food, portions, delivery, or cancelling a meal order, contact your school's meal service provider directly.",
    mealServiceEmailPrefix: "Email: ",
    mealServicePhonePrefix: "Phone: ",
    mealServiceContactMissing:
        "Meal service contact details are not available right now. Please ask your school.",
    platformSectionTitle: "Problem with the app?",
    platformSectionBody:
        "Use this form only for technical platform issues (sign-in, payments, errors on the website).",
    formPanelRegionLabel: "Support request details",
    formPanelExpandedAnnouncement: "The rest of the form is now shown.",
    reasonLabel: "Reason",
    reasonPlaceholder: "Select a reason",
    reasonOptions: {
        technical: "Technical problem with the website / app",
        signIn: "Sign-in / account access",
        payment: "Payments / wallet",
        ordersPackages: "Orders or packages in the app",
        other: "Other",
    },
    messageLabel: "Description",
    messagePlaceholderDefault: "Describe the problem in as much detail as you can…",
    messagePlaceholders: {
        technical: "E.g. the page won't load, I see an error when…",
        signIn: "E.g. I can't sign in, verification email never arrives…",
        payment: "E.g. payment won't complete, my wallet balance didn't update…",
        ordersPackages: "E.g. I can't submit an order, issue with a package…",
        other: "Describe the problem in as much detail as you can…",
    },
    messageRequired: "Please describe the problem.",
    messageTooShort: "Please add more detail about the problem.",
    messageTooLong: `Your message can't be longer than ${PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH} characters.`,
    messageCharCount: (current: number, min: number, max: number) =>
        current < min ? `${current} / ${max} (at least ${min} characters)` : `${current} / ${max}`,
    snapshotsLabel: "Screenshots",
    snapshotsHint: `Optional — up to ${PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT} images (JPEG, PNG, or WebP).`,
    snapshotsAdd: "Add Image",
    snapshotPreviewAlt: (oneBased: number) => `Screenshot ${oneBased} preview`,
    snapshotsRemove: "Remove",
    snapshotsRemoveFor: (oneBased: number) => `Remove screenshot ${oneBased}`,
    snapshotsUploading: "Uploading…",
    snapshotsInvalidType: "Only JPEG, PNG, or WebP images are allowed.",
    snapshotsTooLarge: "That image is too large — try a smaller screenshot.",
    snapshotsUploadFailed: "Couldn't upload the image. Please try again.",
    yourEmailLabel: "We'll reply to the email on your account:",
    submit: "Send",
    submitting: "Sending…",
    successTitle: "Message sent",
    successBody: "We'll reply to the email on your account as soon as we can.",
    sendAnother: "Send another message",
    submitFailed: "We couldn't send your message. Please try again later.",
    privacyPrefix: "For data-protection requests, see our ",
    privacyLinkText: "Privacy Policy",
    leaveDraftTitle: "Leave without sending?",
    leaveDraftMsg: "If you leave now, your message and screenshots will be deleted.",
    leaveDraftYes: "Leave",
};

const platformSupportLangs = { el, en };

export function usePlatformSupportStrings() {
    const [lang] = useContext(langContext);
    return platformSupportLangs[lang];
}

export { PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH };
